import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Column,
  StickyColumnPosition,
  StickyColumnsLayout,
} from '../types';
import { getColumnWidth } from '../utils';

type Props<T> = {
  columns: Column<T>[];
  colWidths: Record<string, number>;
  scrollElementRef: RefObject<HTMLElement>;
  selectionColumnWidth?: number;
  expandColumnWidth?: number;
};

type HiddenContentEdges = {
  isHiddenOnLeft: boolean;
  isHiddenOnRight: boolean;
};

const NO_HIDDEN_CONTENT: HiddenContentEdges = {
  isHiddenOnLeft: false,
  isHiddenOnRight: false,
};

const useStickyColumns = <T>({
  columns,
  colWidths,
  scrollElementRef,
  selectionColumnWidth = 0,
  expandColumnWidth = 0,
}: Props<T>): StickyColumnsLayout | undefined => {
  const [hiddenContent, setHiddenContent] =
    useState<HiddenContentEdges>(NO_HIDDEN_CONTENT);

  const hasStickyColumns = useMemo(
    () => columns.some((c) => !!c.sticky),
    [columns],
  );

  const updateHiddenContent = useCallback(() => {
    const scrollElement = scrollElementRef.current;
    if (!scrollElement) return;

    const { scrollLeft, clientWidth, scrollWidth } = scrollElement;
    const isHiddenOnLeft = scrollLeft > 0;
    const isHiddenOnRight = Math.ceil(scrollLeft + clientWidth) < scrollWidth;

    setHiddenContent((prev) =>
      prev.isHiddenOnLeft === isHiddenOnLeft &&
      prev.isHiddenOnRight === isHiddenOnRight
        ? prev
        : { isHiddenOnLeft, isHiddenOnRight },
    );
  }, [scrollElementRef]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;
    if (!hasStickyColumns || !scrollElement) return undefined;

    updateHiddenContent();
    scrollElement.addEventListener('scroll', updateHiddenContent, {
      passive: true,
    });
    const resizeObserver = new ResizeObserver(updateHiddenContent);
    resizeObserver.observe(scrollElement);

    return () => {
      scrollElement.removeEventListener('scroll', updateHiddenContent);
      resizeObserver.disconnect();
    };
  }, [hasStickyColumns, scrollElementRef, updateHiddenContent]);

  useEffect(() => {
    if (hasStickyColumns) updateHiddenContent();
  }, [hasStickyColumns, columns, colWidths, updateHiddenContent]);

  return useMemo(() => {
    if (!hasStickyColumns) return undefined;

    const positions: Record<string, StickyColumnPosition> = {};
    const leftColumns = columns.filter((c) => c.sticky === 'left');
    const rightColumns = columns.filter((c) => c.sticky === 'right');

    let leftOffset = selectionColumnWidth;
    leftColumns.forEach((column, index) => {
      positions[column.id] = {
        side: 'left',
        offset: leftOffset,
        hasDivider:
          hiddenContent.isHiddenOnLeft && index === leftColumns.length - 1,
      };
      leftOffset += getColumnWidth(column, colWidths);
    });

    let rightOffset = expandColumnWidth;
    [...rightColumns].reverse().forEach((column, reversedIndex) => {
      positions[column.id] = {
        side: 'right',
        offset: rightOffset,
        hasDivider:
          hiddenContent.isHiddenOnRight &&
          reversedIndex === rightColumns.length - 1,
      };
      rightOffset += getColumnWidth(column, colWidths);
    });

    return {
      positions,
      selectionColumn: leftColumns.length
        ? { side: 'left', offset: 0, hasDivider: false }
        : undefined,
      expandColumn: rightColumns.length
        ? { side: 'right', offset: 0, hasDivider: false }
        : undefined,
    };
  }, [
    hasStickyColumns,
    columns,
    colWidths,
    selectionColumnWidth,
    expandColumnWidth,
    hiddenContent,
  ]);
};

export default useStickyColumns;
